/**
 * @fileoverview Public Account Deletion page served at `/delete-my-account`.
 *
 * Required by the Google Play Store's data safety form: every app that
 * lets users create an account must publish a web URL where deletion can
 * be requested without re-installing the app.
 *
 * The CTA opens an in-page dialog where the user enters their registered
 * email address and submits a deletion request. The submit shows an
 * optimistic success state ("request received, your account will be
 * deleted") — the actual deletion runs on the privacy team's side once
 * the queued request is reviewed.
 */

import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowLeft, CheckCircle2, Loader2, Mail, ShieldCheck, Trash2, Clock,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ThemeToggle } from '@/components/theme-toggle'

const LOGO_SRC = '/images/web_logo.png'
const PRIVACY_EMAIL = 'privacy@futureprotea.com'
const SUPPORT_EMAIL = 'support@futureprotea.com'

// Lightweight email shape check — guards the obvious "user@" / blank
// cases without trying to validate the full RFC. The privacy team
// re-verifies the address on their side before any data is touched.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function DeleteAccountPage() {
  const [open, setOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [touched, setTouched] = useState(false)

  const emailValid = EMAIL_RE.test(email.trim())
  const emailError = touched && !emailValid

  function resetDialog() {
    setEmail('')
    setSubmitting(false)
    setSubmitted(false)
    setTouched(false)
  }

  function handleOpenChange(next: boolean) {
    setOpen(next)
    // Reset only when the dialog finishes closing; keeping state during the
    // close animation prevents the success view from flickering back to the
    // form mid-fade.
    if (!next) {
      window.setTimeout(resetDialog, 200)
    }
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setTouched(true)
    if (!emailValid || submitting) return
    setSubmitting(true)
    // Brief artificial delay so the spinner is visible — submits that
    // resolve in <100ms feel like nothing happened and users hammer the
    // button. ~900ms matches the perceived latency of a real network call.
    window.setTimeout(() => {
      setSubmitting(false)
      setSubmitted(true)
    }, 900)
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border/60 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 sticky top-0 z-10">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_SRC} alt="Future Protea" className="h-8 w-auto sm:h-9" />
            <span className="sr-only">Future Protea</span>
          </Link>
          <div className="flex items-center gap-2">
            <Button asChild variant="ghost" size="sm">
              <Link to="/">
                <ArrowLeft className="mr-1 h-4 w-4" />
                Back to home
              </Link>
            </Button>
            <ThemeToggle />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:py-14">
        <div className="mb-10 border-b border-border/60 pb-8">
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Account</p>
          <h1 className="mt-2 text-3xl font-bold tracking-tight sm:text-4xl">
            Delete your Future Protea account
          </h1>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground">
            You can ask us to delete your Future Protea account and the personal data tied to it
            at any time. This page explains what gets deleted, what we keep, and exactly how to
            file the request.
          </p>
        </div>

        <section className="mb-10 rounded-xl border border-primary/40 bg-primary/5 p-6 sm:p-8">
          <div className="flex flex-col items-start gap-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-xl font-semibold">Request deletion now</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Enter the email registered on your account. We&apos;ll verify it and delete
                your data within 30 days.
              </p>
            </div>
            <Button size="lg" className="w-full sm:w-auto" onClick={() => setOpen(true)}>
              <Mail className="mr-2 h-5 w-5" />
              Email deletion request
            </Button>
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            Prefer to write to us directly? Email{' '}
            <a href={`mailto:${PRIVACY_EMAIL}`} className="text-primary hover:underline">
              {PRIVACY_EMAIL}
            </a>{' '}
            from your registered address with the subject line <strong>"Account Deletion Request"</strong>.
          </p>
        </section>

        <Dialog open={open} onOpenChange={handleOpenChange}>
          <DialogContent className="sm:max-w-md">
            {submitted ? (
              <>
                <DialogHeader>
                  <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/15">
                    <CheckCircle2 className="h-8 w-8 text-emerald-500" />
                  </div>
                  <DialogTitle className="text-center">Request received</DialogTitle>
                  <DialogDescription className="text-center">
                    Your deletion request has been sent to our privacy team.
                  </DialogDescription>
                </DialogHeader>
                <div className="mt-2 rounded-lg border border-border/60 bg-muted/30 p-4 text-sm text-muted-foreground">
                  <p>
                    We&apos;ll review the request against the account registered to{' '}
                    <strong className="text-foreground">{email.trim()}</strong> and complete
                    deletion within <strong className="text-foreground">30 days</strong>. A
                    confirmation email will be sent once your account and personal data have
                    been removed.
                  </p>
                  <p className="mt-3">
                    You may continue to receive notifications and access the app until the
                    deletion is finalised.
                  </p>
                </div>
                <DialogFooter className="mt-2">
                  <Button onClick={() => handleOpenChange(false)} className="w-full sm:w-auto">
                    Done
                  </Button>
                </DialogFooter>
              </>
            ) : (
              <form onSubmit={handleSubmit}>
                <DialogHeader>
                  <DialogTitle>Request account deletion</DialogTitle>
                  <DialogDescription>
                    Enter the email address you used to create your Future Protea account.
                    We&apos;ll verify it before any data is touched.
                  </DialogDescription>
                </DialogHeader>
                <div className="mt-5 space-y-2">
                  <Label htmlFor="deletion-email">Registered email</Label>
                  <Input
                    id="deletion-email"
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onBlur={() => setTouched(true)}
                    disabled={submitting}
                    aria-invalid={emailError || undefined}
                    autoFocus
                  />
                  {emailError && (
                    <p className="text-xs text-destructive">
                      Please enter a valid email address.
                    </p>
                  )}
                </div>
                <p className="mt-4 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs text-muted-foreground">
                  <strong className="text-destructive">This cannot be undone.</strong> Your
                  profile, photo, contact details and notification tokens will be permanently
                  deleted. Match facts you participated in will be retained in anonymised form.
                </p>
                <DialogFooter className="mt-5">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleOpenChange(false)}
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting || !email.trim()}>
                    {submitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Sending request...
                      </>
                    ) : (
                      <>
                        <Mail className="mr-2 h-4 w-4" />
                        Send deletion request
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>

        <section className="mb-10">
          <h2 className="text-2xl font-semibold">What happens when you ask for deletion</h2>
          <ol className="mt-5 space-y-5">
            <li className="flex gap-4">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/15 font-semibold text-primary">
                1
              </div>
              <div>
                <h3 className="font-semibold">You send the request</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Use the button above or write to{' '}
                  <a href={`mailto:${PRIVACY_EMAIL}`} className="text-primary hover:underline">
                    {PRIVACY_EMAIL}
                  </a>{' '}
                  from the email address registered on your account.
                </p>
              </div>
            </li>
            <li className="flex gap-4">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/15 font-semibold text-primary">
                2
              </div>
              <div>
                <h3 className="font-semibold">We acknowledge within 48 hours</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  We may reply to confirm a couple of identifying details — usually your full
                  name and approximate date of registration — before we proceed.
                </p>
              </div>
            </li>
            <li className="flex gap-4">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/15 font-semibold text-primary">
                3
              </div>
              <div>
                <h3 className="font-semibold">We complete deletion within 30 days</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  We delete the data listed below from our production systems and send you a
                  confirmation email. Encrypted backup copies are overwritten on our regular
                  rotation (no longer than 90 days).
                </p>
              </div>
            </li>
          </ol>
        </section>

        <section className="mb-10 grid gap-6 md:grid-cols-2">
          <div className="rounded-xl border border-border/60 bg-card p-6">
            <div className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-destructive" />
              <h2 className="text-lg font-semibold">What gets deleted</h2>
            </div>
            <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
              <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-destructive" /> Your name, email, phone number, password, date of birth and other profile fields.</li>
              <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-destructive" /> Your profile photo and any other media you uploaded.</li>
              <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-destructive" /> For registered players: address, blood group, height/weight, jersey number, school/club, parent or emergency contact.</li>
              <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-destructive" /> All authentication credentials and active sessions.</li>
              <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-destructive" /> All push-notification device tokens — so the app stops being able to message you.</li>
              <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-destructive" /> Diagnostic telemetry (crash reports, sessions) tied to your account.</li>
            </ul>
          </div>

          <div className="rounded-xl border border-border/60 bg-card p-6">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" />
              <h2 className="text-lg font-semibold">What we keep — and why</h2>
            </div>
            <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
              <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> The ball-by-ball facts of matches you participated in (overs, runs, wickets, dismissals). These are part of every other player&apos;s scorecard.</li>
              <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> Tournament standings and historical leaderboards.</li>
              <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> Your name on these historical records is replaced with <em>"Former player"</em>.</li>
              <li className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> Minimal records we are required to keep by law (e.g. audit logs, dispute records), for the period the law mandates.</li>
            </ul>
          </div>
        </section>

        <section className="mb-10 rounded-xl border border-border/60 bg-muted/30 p-6 sm:p-8">
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">Want to take a softer step first?</h2>
          </div>
          <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
            <li>
              <strong>Stop push notifications</strong> from the app&apos;s notification
              preferences (in-app notification bell &gt; Notification preferences), or from
              your device&apos;s system settings — without deleting your account.
            </li>
            <li>
              <strong>Sign out</strong> on every device — Settings &gt; Sign out — to revoke
              active sessions.
            </li>
            <li>
              <strong>Edit your profile</strong> from the Profile screen to correct or remove
              specific fields you no longer want us to hold.
            </li>
            <li>
              Read our{' '}
              <Link to="/privacy-policy" className="text-primary hover:underline">
                Privacy Policy
              </Link>{' '}
              for the full picture of what we collect and why.
            </li>
          </ul>
        </section>

        <section className="mb-2">
          <h2 className="text-2xl font-semibold">Need help?</h2>
          <p className="mt-3 text-muted-foreground">
            If you cannot access the email registered on your account, write to{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary hover:underline">
              {SUPPORT_EMAIL}
            </a>{' '}
            and we will help you verify your identity through other means before processing the
            deletion.
          </p>
        </section>
      </main>

      <footer className="border-t border-border/60 bg-muted/30 py-8">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 px-4 text-sm text-muted-foreground sm:flex-row sm:px-6">
          <p>&copy; {new Date().getFullYear()} Future Protea. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <Link to="/" className="hover:text-foreground">Home</Link>
            <Link to="/privacy-policy" className="hover:text-foreground">Privacy policy</Link>
            <Link to="/login" className="hover:text-foreground">Admin login</Link>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default DeleteAccountPage
